import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  sessionStatus, relativeTime, placeOf, toMillis, isOutdated, isNew, isInactive, formatDateTime, initialsOf,
  matchesQuery, deviceMatchesQuery,
} from './sessionsUtils';
import { deviceTitle, deviceSubtitle } from '../../utils/deviceInfo';
import Highlight from './Highlight';

const STATUS_LABEL = { online: 'Online now', recent: 'Recently active', idle: 'Inactive', pending: 'Waiting to sign out' };

function deviceIcon(s) {
  const p = s.device?.platform || '';
  if (/iPad|tablet/.test(p)) return '📲';
  if (/iPhone|Android phone/.test(p)) return '📱';
  return '💻';
}

/** Small ⋯ menu that closes on outside click / Escape. */
function MoreMenu({ items, disabled, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="ses__menu" ref={ref}>
      <button type="button" className="ses__btn ses__btn--icon" aria-haspopup="menu" aria-expanded={open}
        aria-label={label} onClick={() => setOpen((v) => !v)} disabled={disabled}>⋯</button>
      {open && (
        <div className="ses__menu-list" role="menu">
          {items.map((it) => (
            <button key={it.label} type="button" role="menuitem" className={`ses__menu-item${it.danger ? ' is-danger' : ''}`}
              onClick={() => { setOpen(false); it.onClick(); }}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Inline "name this device" editor (owner). */
function RenameField({ session, autoName, onRename }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(session.nickname || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async (name) => {
    setSaving(true); setError('');
    try { await onRename(session, name); setEditing(false); } catch (err) { setError(err.message); }
    setSaving(false);
  };

  if (!editing) {
    return (
      <dd className="ses__rename">
        <span>{session.nickname || autoName}{!session.nickname && <span className="ses__auto"> (automatic)</span>}</span>
        <button type="button" className="ses__linkbtn" onClick={() => { setValue(session.nickname || ''); setEditing(true); }}>✏️ Rename</button>
      </dd>
    );
  }
  return (
    <dd>
      <form className="ses__rename-form" onSubmit={(e) => { e.preventDefault(); save(value); }}>
        <input
          ref={inputRef}
          className="ses__input"
          value={value}
          maxLength={40}
          placeholder={autoName}
          aria-label="Device name"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
        />
        <button type="submit" className="ses__btn ses__btn--sm ses__btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        {session.nickname && (
          <button type="button" className="ses__btn ses__btn--sm" onClick={() => save('')} disabled={saving}>Use automatic</button>
        )}
        <button type="button" className="ses__btn ses__btn--sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
      </form>
      {error && <div className="ses__field-error">{error}</div>}
      <p className="ses__field-hint">Sticks to this device even after it signs out and back in — e.g. “Kitchen iPad”, “Front counter”.</p>
    </dd>
  );
}

function DeviceRow({ s, person, self, now, latest, here, busy, q, onLogout, onRename }) {
  const [open, setOpen] = useState(false);
  const st = sessionStatus(s, now);
  const outdated = isOutdated(s, latest);
  const d = s.device || {};
  const detailsId = `ses-dev-${s.id}`;
  const autoName = deviceTitle({ device: d }, person.name, { self });
  const title = deviceTitle(s, person.name, { self });
  return (
    <li className={`ses__device${st === 'pending' ? ' ses__device--pending' : ''}`}>
      <button type="button" className="ses__dmain" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen((v) => !v)}>
        <span className="ses__dicon" aria-hidden="true">{deviceIcon(s)}</span>
        <span className="ses__dbody">
          <span className="ses__dlabel">
            <Highlight text={title} q={q} />
            {here && <span className="ses__tag ses__tag--green">This device</span>}
            {isNew(s, now) && <span className="ses__tag ses__tag--blue">New</span>}
            {outdated && <span className="ses__tag ses__tag--amber" title="Running an older build than the one deployed">Old version</span>}
            {isInactive(s, now) && <span className="ses__tag">Inactive 30d+</span>}
          </span>
          <span className="ses__dsub"><Highlight text={deviceSubtitle(d) || d.label || 'Unknown device'} q={q} /></span>
          <span className="ses__dmeta">
            <span className={`ses__dot ses__dot--${st}`} /> {STATUS_LABEL[st]}
            {' · '}<Highlight text={placeOf(s.location)} q={q} />
            {' · '}last seen {relativeTime(toMillis(s.lastSeenAt), now)}
          </span>
        </span>
        <span className={`ses__chev${open ? ' is-open' : ''}`} aria-hidden="true">›</span>
      </button>
      {!here && st !== 'pending' && (
        <button type="button" className="ses__btn ses__btn--sm" onClick={onLogout} disabled={busy} aria-label={`Log out ${title}`}>Log out</button>
      )}
      {open && (
        <dl className="ses__details" id={detailsId}>
          <div className="ses__details-wide"><dt>Name</dt><RenameField session={s} autoName={autoName} onRename={onRename} /></div>
          <div><dt>Device</dt><dd>{d.platform || '—'}</dd></div>
          <div><dt>Browser</dt><dd>{d.app ? 'Installed app' : d.browser || '—'}</dd></div>
          <div><dt>App version</dt><dd>{d.version ? `${d.version}${latest ? (outdated ? ' (old)' : ' (latest)') : ''}` : 'Unknown (old)'}</dd></div>
          <div><dt>Location</dt><dd>{placeOf(s.location)}</dd></div>
          <div><dt>IP address</dt><dd>{s.location?.ip || '—'}</dd></div>
          <div><dt>Signed in</dt><dd>{formatDateTime(toMillis(s.createdAt))}</dd></div>
          <div><dt>Last seen</dt><dd>{formatDateTime(toMillis(s.lastSeenAt))}</dd></div>
          {s.revoked && <div><dt>Signed out by owner</dt><dd>{formatDateTime(toMillis(s.revokedAt))}</dd></div>}
        </dl>
      )}
    </li>
  );
}

/**
 * One person: a compact row (who, status, devices at a glance, quick actions)
 * that expands to their devices — the Google Admin / Clerk users-list pattern.
 */
export default function PersonRow({
  person: p, self, open, onToggle, now, latest, mySession, busy, q,
  onLogoutUser, onLogoutDevice, onPassword, onBlock, onRename,
}) {
  const others = p.sessions.filter((s) => !s.revoked && s.id !== mySession).length;
  const live = p.sessions.filter((s) => !s.revoked);
  // Signed out by the owner but not yet confirmed by the device (it hasn't
  // opened the app since). Folded away like Google's "signed out" devices.
  const pending = p.sessions.filter((s) => s.revoked);
  const [showPending, setShowPending] = useState(false);
  // Searching for a device ("iphone", "kitchen", a city): if the person matched
  // only through their devices, show just the matching ones first.
  const [showOthers, setShowOthers] = useState(false);
  useEffect(() => { setShowOthers(false); }, [q]);
  const byDevice = !!q && !matchesQuery({ ...p, sessions: [] }, q);
  const shownLive = byDevice && !showOthers ? live.filter((s) => deviceMatchesQuery(s, q)) : live;
  const hiddenLive = live.length - shownLive.length;
  const online = live.some((s) => sessionStatus(s, now) === 'online');
  const bodyId = `ses-person-${p.uid}`;
  const status = online ? 'Online now'
    : p.activeCount ? `Active ${relativeTime(p.lastSeen, now)}`
      : p.disabled ? 'Blocked' : 'Not signed in';

  return (
    <section className={`ses__row${open ? ' is-open' : ''}${p.disabled ? ' ses__row--blocked' : ''}`} aria-label={p.name}>
      <div className="ses__row-head">
        <button type="button" className="ses__row-toggle" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
          <span className={`ses__chev${open ? ' is-open' : ''}`} aria-hidden="true">›</span>
          <span className={`ses__avatar ses__avatar--${p.role}`} aria-hidden="true">
            {initialsOf(p.name)}
            {online && <span className="ses__avatar-dot" />}
          </span>
          <span className="ses__who-text">
            <span className="ses__name">
              <Highlight text={p.name} q={q} />
              {self && <span className="ses__tag ses__tag--amber">You</span>}
              {p.disabled && <span className="ses__tag ses__tag--red">Blocked</span>}
              {p.mustChangePassword && !p.disabled && <span className="ses__tag ses__tag--blue" title="Must choose their own password at next sign-in">Temporary password</span>}
            </span>
            <span className="ses__meta">
              {p.email && <><Highlight text={p.email} q={q} /> · </>}<span className="ses__role">{p.role}</span>
            </span>
          </span>
          <span className="ses__row-summary">
            <span className={`ses__status${online ? ' is-online' : ''}`}>{status}</span>
            {live.length > 0 && (
              <span className="ses__devstack" aria-label={`${live.length} device${live.length === 1 ? '' : 's'}`}>
                {live.slice(0, 3).map((s) => <span key={s.id} aria-hidden="true">{deviceIcon(s)}</span>)}
                <span className="ses__devcount">{live.length}</span>
              </span>
            )}
          </span>
        </button>
        <div className="ses__row-actions">
          {self ? (
            <>
              <Link to="/account" className="ses__btn ses__btn--sm">🔑 Change my password</Link>
              <button type="button" className="ses__btn ses__btn--sm ses__btn--danger" onClick={onLogoutUser} disabled={busy || !others}>
                Log out other devices
              </button>
            </>
          ) : (
            <>
              <button type="button" className="ses__btn ses__btn--sm" onClick={onPassword} disabled={busy}>🔑 Password</button>
              {/* Only when there is something to sign out — no dead buttons on idle rows. */}
              {p.activeCount > 0 && (
                <button type="button" className="ses__btn ses__btn--sm ses__btn--danger" onClick={onLogoutUser} disabled={busy}>
                  Log out everywhere
                </button>
              )}
              <MoreMenu
                label={`More actions for ${p.name}`}
                disabled={busy}
                items={[p.disabled
                  ? { label: '✅ Unblock sign-in', onClick: onBlock }
                  : { label: '🚫 Block sign-in', onClick: onBlock, danger: true }]}
              />
            </>
          )}
        </div>
      </div>

      {open && (
        <div id={bodyId} className="ses__row-body">
          {shownLive.length > 0 && (
            <ul className="ses__devices">
              {shownLive.map((s) => (
                <DeviceRow key={s.id} s={s} person={p} self={self} now={now} latest={latest} here={s.id === mySession}
                  busy={busy} q={q} onLogout={() => onLogoutDevice(s)} onRename={onRename} />
              ))}
            </ul>
          )}
          {hiddenLive > 0 && (
            <button type="button" className="ses__pending-toggle" onClick={() => setShowOthers(true)}>
              <span className="ses__chev" aria-hidden="true">›</span>
              Show {hiddenLive} other device{hiddenLive === 1 ? '' : 's'}
            </button>
          )}
          {!live.length && (
            <p className="ses__nodevices">{p.disabled ? 'Blocked — can’t sign in.' : 'Not signed in on any device.'}</p>
          )}
          {pending.length > 0 && (
            <div className="ses__pending">
              <button type="button" className="ses__pending-toggle" aria-expanded={showPending} onClick={() => setShowPending((v) => !v)}>
                <span className={`ses__chev${showPending ? ' is-open' : ''}`} aria-hidden="true">›</span>
                {pending.length} signed-out device{pending.length === 1 ? '' : 's'} waiting to confirm
                <span className="ses__auto"> — they finish signing out the next time they open the app</span>
              </button>
              {showPending && (
                <ul className="ses__devices">
                  {pending.map((s) => (
                    <DeviceRow key={s.id} s={s} person={p} self={self} now={now} latest={latest} here={false}
                      busy={busy} q={q} onLogout={() => {}} onRename={onRename} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
